import { useEffect } from 'react';
import { useAccent, useSession } from './lib/session';
import { useRouter } from './lib/router';
import { Spinner } from './ui/primitives';
import { LandingScreen } from './screens/Landing';
import { AuthScreen } from './screens/Auth';
import { HomeScreen } from './screens/Home';
import { SettingsScreen } from './screens/Settings';
import { BoardScreen } from './screens/Board';
import { InviteScreen } from './screens/Invite';
import { ForgotScreen, ResetScreen } from './screens/Recover';
import { PrivacyScreen } from './screens/Privacy';
import { postAuthRoute } from './lib/pendingInvite';
import { takeCarriedSandbox } from './board/sandbox';
import { api } from './lib/api';
import { color } from './tokens';

/**
 * Application shell.
 *
 * Resolves the session once, then routes. Screens that require an account
 * redirect rather than rendering a signed-out shell, so a bookmarked `/app`
 * lands on sign-in and comes back after.
 */
export function App() {
  const { route, navigate } = useRouter();
  const accent = useAccent();
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);
  const bootstrap = useSession((s) => s.bootstrap);

  useEffect(() => {
    document.documentElement.style.setProperty('--sp-accent', accent);
  }, [accent]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  /**
   * Bring demo work into a new account.
   *
   * Someone who played in the sandbox and then signed up should find their
   * board waiting rather than starting over. It arrives through the ordinary
   * import path, so it is validated exactly like any other board file.
   */
  useEffect(() => {
    if (!user) return;
    const carried = takeCarriedSandbox();
    if (!carried) return;

    void api
      .importBoard({ scribbleplot: 1, name: 'My first board', doc: carried, assets: [] })
      .then(({ board }) => navigate({ name: 'board', id: board.id }, { replace: true }))
      // A failed import is not worth blocking sign-up over; the demo is still
      // in local storage either way.
      .catch(() => undefined);
  }, [user, navigate]);

  // Guard the authenticated routes once the session is known.
  useEffect(() => {
    if (loading) return;
    const needsAccount = route.name === 'home' || route.name === 'settings' || route.name === 'board';
    if (needsAccount && !user) navigate({ name: 'signin' }, { replace: true });

    // Someone who arrived via an invite link goes back to it, not to the board
    // list they have nothing in yet.
    if ((route.name === 'signin' || route.name === 'signup') && user) {
      navigate(postAuthRoute(), { replace: true });
    }
  }, [loading, user, route.name, navigate]);

  if (loading) return <Spinner accent={accent} label="Starting Scribbleplot" />;

  switch (route.name) {
    case 'landing':
      return <LandingScreen accent={accent} navigate={navigate} signedIn={Boolean(user)} />;

    case 'signin':
    case 'signup':
      return <AuthScreen mode={route.name === 'signup' ? 'up' : 'in'} accent={accent} navigate={navigate} />;

    case 'home':
      return user ? <HomeScreen accent={accent} navigate={navigate} /> : <Spinner accent={accent} />;

    case 'settings':
      return user ? <SettingsScreen accent={accent} navigate={navigate} /> : <Spinner accent={accent} />;

    case 'board':
      return user ? (
        <BoardScreen target={{ kind: 'id', id: route.id }} navigate={navigate} />
      ) : (
        <Spinner accent={accent} />
      );

    // The demo needs no account, so it sits outside the guarded routes.
    case 'demo':
      return <BoardScreen target={{ kind: 'demo' }} navigate={navigate} />;

    case 'forgot':
      return <ForgotScreen accent={accent} navigate={navigate} />;

    case 'privacy':
      return <PrivacyScreen accent={accent} navigate={navigate} />;

    // A reset link must work even for someone with a stale session cookie.
    case 'reset':
      return <ResetScreen token={route.token} accent={accent} navigate={navigate} />;

    // Invite links resolve before sign-in, so the recipient can see what they
    // are being offered.
    case 'invite':
      return <InviteScreen token={route.token} accent={accent} navigate={navigate} />;

    // Public share links work without an account.
    case 'public':
      return <BoardScreen target={{ kind: 'slug', slug: route.slug }} navigate={navigate} />;

    case 'notfound':
      return <NotFound accent={accent} onHome={() => navigate({ name: 'landing' })} />;
  }
}

function NotFound({ accent, onHome }: { accent: string; onHome: () => void }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
      }}
    >
      <div style={{ width: 22, height: 22, borderRadius: 7, background: accent }} />
      <div style={{ fontSize: 19, fontWeight: 700 }}>That page does not exist.</div>
      <button
        type="button"
        onClick={onHome}
        style={{
          height: 34,
          padding: '0 16px',
          borderRadius: 9,
          border: `1px solid ${color.lineStrong}`,
          background: 'transparent',
          color: color.text,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Go to scribbleplot.com
      </button>
    </div>
  );
}
